import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  ActivityIndicator,
  Dimensions,
  Platform,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useCompanyAccountingStore, ProfitTrend, VehicleProfitability } from '../../stores/companyAccountingStore';
import { CompanyAccountingSummary } from '../../types';
import theme from '../../theme';

const isWeb = Platform.OS === 'web';
const screenWidth = Dimensions.get('window').width;

interface CompanyProfitScreenProps {
  navigation?: any;
}

export const CompanyProfitScreen: React.FC<CompanyProfitScreenProps> = ({ navigation }) => {
  // Store hooks
  const {
    selectedSummary,
    profitTrends,
    vehicleProfitability,
    loading,
    error,
    fetchSummaryByPeriod,
    fetchProfitTrends,
    fetchVehicleProfitability,
    calculateCompanyProfit,
    clearError,
  } = useCompanyAccountingStore();

  // State
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [trendMonths, setTrendMonths] = useState(6); // Show 6 months of trend
  const [refreshing, setRefreshing] = useState(false);

  // Load data on mount and when month/year changes
  useEffect(() => {
    loadDashboardData();
  }, [selectedMonth, selectedYear, trendMonths]);

  const loadDashboardData = async () => {
    setRefreshing(true);
    try {
      // Load current month summary
      await fetchSummaryByPeriod(selectedYear, selectedMonth);
      
      // Load profit trends (last N months)
      await fetchProfitTrends(trendMonths);
      
      // Load vehicle profitability for current month
      await fetchVehicleProfitability(selectedYear, selectedMonth);
    } catch (err) {
      console.error('Error loading dashboard data:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const handleRecalculate = async () => {
    try {
      setRefreshing(true);
      await calculateCompanyProfit(selectedYear, selectedMonth);
      await loadDashboardData();
    } catch (err: any) {
      console.error('Recalculation error:', err);
    } finally {
      setRefreshing(false);
    }
  };

  const goToPreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getMonthName = (month: number) => {
    return new Date(2000, month - 1).toLocaleDateString('en-US', { month: 'long' });
  };

  const getShortMonthName = (month: number) => {
    return new Date(2000, month - 1).toLocaleDateString('en-US', { month: 'short' });
  };

  const getProfitColor = (profit: number) => {
    if (profit > 0) return theme.colors.success;
    if (profit < 0) return theme.colors.error;
    return theme.colors.textSecondary;
  };

  const renderHeader = () => (
    <View style={styles.header}>
      <View>
        <Text style={styles.headerTitle}>Company Profit Dashboard</Text>
        <Text style={styles.headerSubtitle}>Financial performance overview</Text>
      </View>
      <TouchableOpacity
        style={styles.recalculateButton}
        onPress={handleRecalculate}
        disabled={refreshing}
      >
        {refreshing ? (
          <ActivityIndicator size="small" color="#FFFFFF" />
        ) : (
          <>
            <Ionicons name="refresh-outline" size={18} color="#FFFFFF" />
            <Text style={styles.recalculateText}>Recalculate</Text>
          </>
        )}
      </TouchableOpacity>
    </View>
  );

  const renderMonthSelector = () => (
    <View style={styles.monthSelector}>
      <TouchableOpacity style={styles.monthArrow} onPress={goToPreviousMonth}>
        <Ionicons name="chevron-back" size={24} color={theme.colors.textPrimary} />
      </TouchableOpacity>
      <View style={styles.monthDisplay}>
        <Text style={styles.monthText}>{getMonthName(selectedMonth)} {selectedYear}</Text>
        {selectedSummary && (
          <Text style={styles.calculatedText}>
            Calculated: {new Date(selectedSummary.calculated_at).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </Text>
        )}
      </View>
      <TouchableOpacity style={styles.monthArrow} onPress={goToNextMonth}>
        <Ionicons name="chevron-forward" size={24} color={theme.colors.textPrimary} />
      </TouchableOpacity>
    </View>
  );

  const renderMetricsGrid = () => {
    if (!selectedSummary) {
      return (
        <View style={styles.noDataCard}>
          <Ionicons name="analytics-outline" size={48} color={theme.colors.textSecondary} />
          <Text style={styles.noDataText}>No Data Available</Text>
          <Text style={styles.noDataSubtext}>
            No profit calculation for {getMonthName(selectedMonth)} {selectedYear}
          </Text>
          <TouchableOpacity style={styles.calculateButton} onPress={handleRecalculate}>
            <Text style={styles.calculateButtonText}>Calculate Now</Text>
          </TouchableOpacity>
        </View>
      );
    }

    return (
      <View style={styles.metricsGrid}>
        {/* Gross Profit */}
        <View style={[styles.metricCard, styles.largeMetric]}>
          <View style={styles.metricHeader}>
            <Ionicons name="trending-up-outline" size={24} color={getProfitColor(selectedSummary.gross_profit)} />
            <Text style={styles.metricLabel}>Gross Profit</Text>
          </View>
          <Text style={[styles.metricValue, { color: getProfitColor(selectedSummary.gross_profit) }]}>
            {formatCurrency(selectedSummary.gross_profit)}
          </Text>
          <Text style={styles.metricSubtext}>
            Rent Income - Vehicle Costs
          </Text>
        </View>

        {/* Net Profit */}
        <View style={[styles.metricCard, styles.largeMetric]}>
          <View style={styles.metricHeader}>
            <Ionicons name="cash-outline" size={24} color={getProfitColor(selectedSummary.net_profit)} />
            <Text style={styles.metricLabel}>Net Profit</Text>
          </View>
          <Text style={[styles.metricValue, { color: getProfitColor(selectedSummary.net_profit) }]}>
            {formatCurrency(selectedSummary.net_profit)}
          </Text>
          <Text style={styles.metricSubtext}>
            After Other Income/Expenses
          </Text>
        </View>

        {/* Profit Per Vehicle */}
        <View style={styles.metricCard}>
          <View style={styles.metricHeader}>
            <Ionicons name="car-outline" size={20} color={theme.colors.primary} />
            <Text style={styles.metricLabel}>Per Vehicle</Text>
          </View>
          <Text style={styles.metricValue}>{formatCurrency(selectedSummary.profit_per_vehicle)}</Text>
        </View>

        {/* Vehicle Count */}
        <View style={styles.metricCard}>
          <View style={styles.metricHeader}>
            <Ionicons name="apps-outline" size={20} color={theme.colors.primary} />
            <Text style={styles.metricLabel}>Vehicles</Text>
          </View>
          <Text style={styles.metricValue}>{selectedSummary.vehicle_count}</Text>
        </View>

        {/* Driver Count */}
        <View style={styles.metricCard}>
          <View style={styles.metricHeader}>
            <Ionicons name="people-outline" size={20} color={theme.colors.primary} />
            <Text style={styles.metricLabel}>Drivers</Text>
          </View>
          <Text style={styles.metricValue}>{selectedSummary.active_driver_count}</Text>
        </View>

        {/* Total Revenue */}
        <View style={styles.metricCard}>
          <View style={styles.metricHeader}>
            <Ionicons name="wallet-outline" size={20} color={theme.colors.success} />
            <Text style={styles.metricLabel}>Company Share Income</Text>
          </View>
          <Text style={[styles.metricValue, { color: theme.colors.success }]}>
            {formatCurrency(selectedSummary.total_driver_rent_income)}
          </Text>
        </View>

        {/* Total Costs */}
        <View style={styles.metricCard}>
          <View style={styles.metricHeader}>
            <Ionicons name="card-outline" size={20} color={theme.colors.error} />
            <Text style={styles.metricLabel}>Vehicle Costs</Text>
          </View>
          <Text style={[styles.metricValue, { color: theme.colors.error }]}>
            {formatCurrency(selectedSummary.total_vehicle_costs)}
          </Text>
        </View>

        {/* Other Income */}
        {(selectedSummary.total_other_income ?? 0) > 0 && (
          <View style={styles.metricCard}>
            <View style={styles.metricHeader}>
              <Ionicons name="add-circle-outline" size={20} color={theme.colors.success} />
              <Text style={styles.metricLabel}>Other Income</Text>
            </View>
            <Text style={[styles.metricValue, { color: theme.colors.success }]}>
              {formatCurrency(selectedSummary.total_other_income ?? 0)}
            </Text>
          </View>
        )}

        {/* Other Expenses */}
        {(selectedSummary.total_other_expenses ?? 0) > 0 && (
          <View style={styles.metricCard}>
            <View style={styles.metricHeader}>
              <Ionicons name="remove-circle-outline" size={20} color={theme.colors.error} />
              <Text style={styles.metricLabel}>Other Expenses</Text>
            </View>
            <Text style={[styles.metricValue, { color: theme.colors.error }]}>
              {formatCurrency(selectedSummary.total_other_expenses ?? 0)}
            </Text>
          </View>
        )}
      </View>
    );
  };

  const renderProfitTrendChart = () => {
    if (profitTrends.length === 0) {
      return (
        <View style={styles.chartCard}>
          <Text style={styles.chartTitle}>Profit Trend (Last {trendMonths} Months)</Text>
          <View style={styles.noDataContainer}>
            <Ionicons name="bar-chart-outline" size={48} color={theme.colors.textSecondary} />
            <Text style={styles.noDataText}>No trend data available</Text>
          </View>
        </View>
      );
    }

    // Find max value for scaling
    const maxProfit = Math.max(...profitTrends.map(t => Math.max(t.gross_profit, t.net_profit)));
    const minProfit = Math.min(...profitTrends.map(t => Math.min(t.gross_profit, t.net_profit)), 0);
    const range = maxProfit - minProfit;
    const chartHeight = 200;

    const getBarHeight = (value: number) => {
      if (range === 0) return 0;
      const normalized = (value - minProfit) / range;
      return normalized * chartHeight;
    };

    const barWidth = 40;
    const barGap = 20;

    return (
      <View style={styles.chartCard}>
        <View style={styles.chartHeader}>
          <Text style={styles.chartTitle}>Profit Trend</Text>
          <View style={styles.trendSelector}>
            {[3, 6, 12].map((months) => (
              <TouchableOpacity
                key={months}
                style={[styles.trendButton, trendMonths === months && styles.trendButtonActive]}
                onPress={() => setTrendMonths(months)}
              >
                <Text style={[styles.trendButtonText, trendMonths === months && styles.trendButtonTextActive]}>
                  {months}M
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        {/* Chart Legend */}
        <View style={styles.chartLegend}>
          <View style={styles.legendItem}>
            <View style={[styles.legendDot, { backgroundColor: theme.colors.primary }]} />
            <Text style={styles.legendText}>Gross Profit</Text>
          </View>
          <View style={styles.legendItem}>
            <View style={[styles.legendDot, { backgroundColor: theme.colors.success }]} />
            <Text style={styles.legendText}>Net Profit</Text>
          </View>
        </View>

        {/* Chart */}
        <ScrollView horizontal showsHorizontalScrollIndicator={false} style={styles.chartScroll}>
          <View style={styles.chartContainer}>
            {/* Y-axis labels */}
            <View style={styles.yAxis}>
              <Text style={styles.axisLabel}>{formatCurrency(maxProfit)}</Text>
              <Text style={styles.axisLabel}>{formatCurrency((maxProfit + minProfit) / 2)}</Text>
              <Text style={styles.axisLabel}>{formatCurrency(minProfit)}</Text>
            </View>

            {/* Bars */}
            <View style={styles.barsContainer}>
              {profitTrends.map((trend, index) => {
                const grossHeight = getBarHeight(trend.gross_profit);
                const netHeight = getBarHeight(trend.net_profit);

                return (
                  <View key={trend.period} style={styles.barGroup}>
                    <View style={styles.barPair}>
                      <View
                        style={[
                          styles.bar,
                          {
                            height: grossHeight,
                            backgroundColor: theme.colors.primary,
                            width: barWidth / 2 - 2,
                          },
                        ]}
                      />
                      <View
                        style={[
                          styles.bar,
                          {
                            height: netHeight,
                            backgroundColor: theme.colors.success,
                            width: barWidth / 2 - 2,
                          },
                        ]}
                      />
                    </View>
                    <Text style={styles.barLabel}>
                      {getShortMonthName(trend.month)} '{trend.year.toString().slice(-2)}
                    </Text>
                  </View>
                );
              })}
            </View>
          </View>
        </ScrollView>

        {/* Trend Statistics */}
        <View style={styles.trendStats}>
          <View style={styles.trendStat}>
            <Text style={styles.trendStatLabel}>Average Net Profit</Text>
            <Text style={styles.trendStatValue}>
              {formatCurrency(profitTrends.reduce((sum, t) => sum + t.net_profit, 0) / profitTrends.length)}
            </Text>
          </View>
          <View style={styles.trendStat}>
            <Text style={styles.trendStatLabel}>Total Net Profit</Text>
            <Text style={styles.trendStatValue}>
              {formatCurrency(profitTrends.reduce((sum, t) => sum + t.net_profit, 0))}
            </Text>
          </View>
        </View>
      </View>
    );
  };

  const renderVehicleProfitability = () => {
    if (vehicleProfitability.length === 0) {
      return (
        <View style={styles.chartCard}>
          <Text style={styles.chartTitle}>Vehicle Profitability</Text>
          <View style={styles.noDataContainer}>
            <Ionicons name="car-sport-outline" size={48} color={theme.colors.textSecondary} />
            <Text style={styles.noDataText}>No vehicle data available</Text>
          </View>
        </View>
      );
    }

    // Sort by profit descending
    const sortedVehicles = [...vehicleProfitability].sort((a, b) => b.profit - a.profit);

    return (
      <View style={styles.chartCard}>
        <Text style={styles.chartTitle}>
          Vehicle Profitability ({getMonthName(selectedMonth)} {selectedYear})
        </Text>

        <View style={styles.vehicleTable}>
          {/* Table Header */}
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderText, styles.vehicleCol]}>Vehicle</Text>
            <Text style={[styles.tableHeaderText, styles.countCol]}>Count</Text>
            <Text style={[styles.tableHeaderText, styles.amountCol]}>Cost</Text>
            <Text style={[styles.tableHeaderText, styles.amountCol]}>Income</Text>
            <Text style={[styles.tableHeaderText, styles.amountCol]}>Profit</Text>
            <Text style={[styles.tableHeaderText, styles.avgCol]}>Avg/Vehicle</Text>
          </View>

          {/* Table Rows */}
          <ScrollView style={styles.tableBody} nestedScrollEnabled>
            {sortedVehicles.map((vehicle, index) => (
              <View
                key={`${vehicle.make_model}-${vehicle.year}`}
                style={[styles.tableRow, index % 2 === 0 && styles.tableRowEven]}
              >
                <View style={styles.vehicleCol}>
                  <Text style={styles.vehicleName}>{vehicle.make_model}</Text>
                  <Text style={styles.vehicleYear}>{vehicle.year}</Text>
                </View>
                <Text style={styles.countCol}>{vehicle.vehicle_count}</Text>
                <Text style={[styles.amountCol, styles.costText]}>
                  {formatCurrency(vehicle.total_cost)}
                </Text>
                <Text style={[styles.amountCol, styles.incomeText]}>
                  {formatCurrency(vehicle.total_rent_income)}
                </Text>
                <Text style={[styles.amountCol, { color: getProfitColor(vehicle.profit) }]}>
                  {formatCurrency(vehicle.profit)}
                </Text>
                <Text style={[styles.avgCol, { color: getProfitColor(vehicle.profit_per_vehicle) }]}>
                  {formatCurrency(vehicle.profit_per_vehicle)}
                </Text>
              </View>
            ))}
          </ScrollView>

          {/* Table Footer - Totals */}
          <View style={styles.tableFooter}>
            <Text style={styles.vehicleCol}>Total ({sortedVehicles.length} types)</Text>
            <Text style={styles.countCol}>
              {sortedVehicles.reduce((sum, v) => sum + v.vehicle_count, 0)}
            </Text>
            <Text style={[styles.amountCol, styles.costText]}>
              {formatCurrency(sortedVehicles.reduce((sum, v) => sum + v.total_cost, 0))}
            </Text>
            <Text style={[styles.amountCol, styles.incomeText]}>
              {formatCurrency(sortedVehicles.reduce((sum, v) => sum + v.total_rent_income, 0))}
            </Text>
            <Text
              style={[
                styles.amountCol,
                { color: getProfitColor(sortedVehicles.reduce((sum, v) => sum + v.profit, 0)) },
              ]}
            >
              {formatCurrency(sortedVehicles.reduce((sum, v) => sum + v.profit, 0))}
            </Text>
            <Text style={styles.avgCol}>-</Text>
          </View>
        </View>
      </View>
    );
  };

  const renderBreakdownSection = () => {
    if (!selectedSummary) return null;

    return (
      <View style={styles.breakdownCard}>
        <Text style={styles.chartTitle}>Profit Calculation Breakdown</Text>

        <View style={styles.breakdownSection}>
          <Text style={styles.breakdownSectionTitle}>Revenue</Text>
          <View style={styles.breakdownRow}>
            <Text style={styles.breakdownLabel}>Driver Company Share</Text>
            <Text style={[styles.breakdownValue, styles.incomeText]}>
              +{formatCurrency(selectedSummary.total_driver_rent_income)}
            </Text>
          </View>
          {(selectedSummary.total_other_income ?? 0) > 0 && (
            <View style={styles.breakdownRow}>
              <Text style={styles.breakdownLabel}>Other Income</Text>
              <Text style={[styles.breakdownValue, styles.incomeText]}>
                +{formatCurrency(selectedSummary.total_other_income ?? 0)}
              </Text>
            </View>
          )}
        </View>

        <View style={styles.breakdownDivider} />

        <View style={styles.breakdownSection}>
          <Text style={styles.breakdownSectionTitle}>Costs</Text>
          <View style={styles.breakdownRow}>
            <Text style={styles.breakdownLabel}>Vehicle Costs to Owners</Text>
            <Text style={[styles.breakdownValue, styles.costText]}>
              -{formatCurrency(selectedSummary.total_vehicle_costs)}
            </Text>
          </View>
          {(selectedSummary.total_other_expenses ?? 0) > 0 && (
            <View style={styles.breakdownRow}>
              <Text style={styles.breakdownLabel}>Other Expenses</Text>
              <Text style={[styles.breakdownValue, styles.costText]}>
                -{formatCurrency(selectedSummary.total_other_expenses ?? 0)}
              </Text>
            </View>
          )}
        </View>

        <View style={styles.breakdownDivider} />

        <View style={styles.breakdownSection}>
          <Text style={styles.breakdownSectionTitle}>Profit Summary</Text>
          <View style={styles.breakdownRow}>
            <Text style={styles.breakdownLabel}>Gross Profit</Text>
            <Text style={[styles.breakdownValue, { color: getProfitColor(selectedSummary.gross_profit) }]}>
              {formatCurrency(selectedSummary.gross_profit)}
            </Text>
          </View>
          <View style={styles.breakdownRow}>
            <Text style={styles.breakdownLabel}>Net Profit</Text>
            <Text style={[styles.breakdownValue, { color: getProfitColor(selectedSummary.net_profit), fontWeight: '700' }]}>
              {formatCurrency(selectedSummary.net_profit)}
            </Text>
          </View>
        </View>

        {selectedSummary.notes && (
          <>
            <View style={styles.breakdownDivider} />
            <View style={styles.notesSection}>
              <Ionicons name="information-circle-outline" size={18} color={theme.colors.textSecondary} />
              <Text style={styles.notesText}>{selectedSummary.notes}</Text>
            </View>
          </>
        )}
      </View>
    );
  };

  return (
    <View style={styles.container}>
      {renderHeader()}
      {renderMonthSelector()}

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {refreshing && !selectedSummary ? (
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color={theme.colors.primary} />
            <Text style={styles.loadingText}>Loading dashboard data...</Text>
          </View>
        ) : (
          <>
            {renderMetricsGrid()}
            {renderProfitTrendChart()}
            {renderVehicleProfitability()}
            {renderBreakdownSection()}
          </>
        )}
      </ScrollView>

      {/* Error Banner */}
      {error && (
        <View style={styles.errorBanner}>
          <Text style={styles.errorText}>{error}</Text>
          <TouchableOpacity onPress={clearError}>
            <Ionicons name="close" size={20} color="#FFFFFF" />
          </TouchableOpacity>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: theme.colors.background,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    backgroundColor: theme.colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  headerTitle: {
    fontSize: 24,
    fontWeight: '700',
    color: theme.colors.textPrimary,
  },
  headerSubtitle: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    marginTop: 4,
  },
  recalculateButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: theme.colors.primary,
    paddingHorizontal: 16,
    paddingVertical: 10,
    borderRadius: 8,
    gap: 6,
  },
  recalculateText: {
    color: '#FFFFFF',
    fontSize: 14,
    fontWeight: '600',
  },
  monthSelector: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 16,
    backgroundColor: theme.colors.surface,
    borderBottomWidth: 1,
    borderBottomColor: theme.colors.border,
  },
  monthArrow: {
    padding: 8,
  },
  monthDisplay: {
    alignItems: 'center',
  },
  monthText: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.textPrimary,
  },
  calculatedText: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    marginTop: 4,
  },
  content: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 64,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: theme.colors.textSecondary,
  },
  noDataCard: {
    backgroundColor: theme.colors.surface,
    margin: 16,
    padding: 32,
    borderRadius: 12,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  noDataText: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.textPrimary,
    marginTop: 16,
  },
  noDataSubtext: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    marginTop: 8,
    textAlign: 'center',
  },
  calculateButton: {
    marginTop: 24,
    backgroundColor: theme.colors.primary,
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  calculateButtonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  metricsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    padding: 16,
    gap: 12,
  },
  metricCard: {
    backgroundColor: theme.colors.surface,
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: theme.colors.border,
    width: screenWidth < 768 ? '100%' : (screenWidth - 44) / 2, // 32 padding + 12 gap
  },
  largeMetric: {
    width: '100%',
  },
  metricHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
    gap: 8,
  },
  metricLabel: {
    fontSize: 14,
    color: theme.colors.textSecondary,
    fontWeight: '500',
  },
  metricValue: {
    fontSize: 32,
    fontWeight: '700',
    color: theme.colors.textPrimary,
    marginBottom: 4,
  },
  metricSubtext: {
    fontSize: 12,
    color: theme.colors.textSecondary,
  },
  chartCard: {
    backgroundColor: theme.colors.surface,
    margin: 16,
    marginTop: 0,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  chartHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  chartTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: theme.colors.textPrimary,
  },
  trendSelector: {
    flexDirection: 'row',
    gap: 8,
  },
  trendButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 6,
    backgroundColor: theme.colors.background,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  trendButtonActive: {
    backgroundColor: theme.colors.primary,
    borderColor: theme.colors.primary,
  },
  trendButtonText: {
    fontSize: 12,
    color: theme.colors.textPrimary,
  },
  trendButtonTextActive: {
    color: '#FFFFFF',
    fontWeight: '600',
  },
  chartLegend: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 24,
    marginBottom: 16,
  },
  legendItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  legendDot: {
    width: 12,
    height: 12,
    borderRadius: 6,
  },
  legendText: {
    fontSize: 12,
    color: theme.colors.textSecondary,
  },
  chartScroll: {
    marginBottom: 16,
  },
  chartContainer: {
    flexDirection: 'row',
    height: 200,
  },
  yAxis: {
    width: 80,
    justifyContent: 'space-between',
    paddingRight: 8,
  },
  axisLabel: {
    fontSize: 10,
    color: theme.colors.textSecondary,
    textAlign: 'right',
  },
  barsContainer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 20,
  },
  barGroup: {
    alignItems: 'center',
  },
  barPair: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 4,
    marginBottom: 8,
  },
  bar: {
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
    minHeight: 2,
  },
  barLabel: {
    fontSize: 10,
    color: theme.colors.textSecondary,
    marginTop: 4,
  },
  trendStats: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: theme.colors.border,
  },
  trendStat: {
    alignItems: 'center',
  },
  trendStatLabel: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    marginBottom: 4,
  },
  trendStatValue: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.colors.textPrimary,
  },
  noDataContainer: {
    alignItems: 'center',
    padding: 32,
  },
  vehicleTable: {
    marginTop: 16,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: theme.colors.background,
    paddingVertical: 12,
    paddingHorizontal: 8,
    borderRadius: 8,
    marginBottom: 8,
  },
  tableHeaderText: {
    fontSize: 12,
    fontWeight: '600',
    color: theme.colors.textSecondary,
  },
  vehicleCol: {
    flex: 2,
  },
  countCol: {
    width: 50,
    textAlign: 'center',
  },
  amountCol: {
    flex: 1,
    textAlign: 'right',
  },
  avgCol: {
    flex: 1,
    textAlign: 'right',
  },
  tableBody: {
    maxHeight: 400,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 12,
    paddingHorizontal: 8,
    alignItems: 'center',
  },
  tableRowEven: {
    backgroundColor: theme.colors.background,
  },
  vehicleName: {
    fontSize: 14,
    fontWeight: '500',
    color: theme.colors.textPrimary,
  },
  vehicleYear: {
    fontSize: 12,
    color: theme.colors.textSecondary,
    marginTop: 2,
  },
  costText: {
    color: theme.colors.error,
  },
  incomeText: {
    color: theme.colors.success,
  },
  tableFooter: {
    flexDirection: 'row',
    paddingVertical: 12,
    paddingHorizontal: 8,
    borderTopWidth: 2,
    borderTopColor: theme.colors.border,
    backgroundColor: theme.colors.background,
    marginTop: 8,
    borderRadius: 8,
  },
  breakdownCard: {
    backgroundColor: theme.colors.surface,
    margin: 16,
    marginTop: 0,
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: theme.colors.border,
  },
  breakdownSection: {
    marginBottom: 16,
  },
  breakdownSectionTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: theme.colors.textSecondary,
    marginBottom: 12,
    textTransform: 'uppercase',
  },
  breakdownRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
  },
  breakdownLabel: {
    fontSize: 14,
    color: theme.colors.textPrimary,
  },
  breakdownValue: {
    fontSize: 14,
    fontWeight: '500',
  },
  breakdownDivider: {
    height: 1,
    backgroundColor: theme.colors.border,
    marginVertical: 16,
  },
  notesSection: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 8,
    padding: 12,
    backgroundColor: theme.colors.background,
    borderRadius: 8,
  },
  notesText: {
    flex: 1,
    fontSize: 14,
    color: theme.colors.textPrimary,
    lineHeight: 20,
  },
  errorBanner: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: theme.colors.error,
    padding: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  errorText: {
    flex: 1,
    color: '#FFFFFF',
    fontSize: 14,
  },
});

export default CompanyProfitScreen;
